import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider } from './context/AuthContext';
import { TranslationProvider } from './context/TranslationContext';
import { TooltipProvider } from './components/ui/tooltip';
import { MainLayout } from './components/layout/MainLayout';
import { DPAPage } from './pages/Landing/DPAPage';
import { HomePage } from './pages/Landing/HomePage';
import { LoginPage } from './pages/Landing/LoginPage';
import { ContactPage } from './pages/Landing/ContactPage';
import { Sidebar } from './components/layout/Sidebar';
import { UserManagementPage } from './pages/App/UserManagementPage';
import { ForgottenPasswordPage } from './pages/Landing/ForgottenPasswordPage';
import { NewPasswordPage } from './pages/Landing/NewPasswordPage';
import { TaskDashboardPage } from './pages/App/TaskDashboardPage';
import { TaskDescriptionPage } from './pages/App/TaskDescriptionPage';
import { AdminSettingsPage } from './pages/App/AdminSettingsPage';
import { ProductivityHubPage } from './pages/App/ProductivityHubPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ProspectsManagementPage } from './pages/App/ProspectsManagementPage';
import { ContactsManagementPage } from './pages/App/ContactsManagementPage';
import { DomainServicesPage } from './pages/App/DomainServicesPage';
import { AdblockBypassPage } from './pages/App/AdblockBypassPage';
import { SSPDashboardPage } from './pages/App/SSPDashboardPage';
import { NoConsentDashboardPage } from './pages/App/NoConsentDashboardPage';
import { ExchangeRatesPage } from './pages/App/ExchangeRatesPage';
import { AdblockLegacyDashboardPage } from './pages/App/AdblockLegacyDashboardPage';
import { OverviewPage } from './pages/App/OverviewPage';
// Import i18n pour initialiser les traductions, on ne l'utilise pas directement
import './i18n';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <TranslationProvider>
            <TooltipProvider>
              <MainLayout>
                <Routes>
                  {/* Routes publiques */}
                  <Route path="/" element={<HomePage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/dpa" element={<DPAPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/forgot-password" element={<ForgottenPasswordPage />} />
                  <Route path="/reset-password/:token" element={<NewPasswordPage />} />
                  
                  {/* Routes protégées pour tous les utilisateurs authentifiés */}
                  {/* Route pour Adblock Contournement - accessible à tous les utilisateurs authentifiés */}
                  <Route
                    path="/app/adblock-bypass"
                    element={
                      <ProtectedRoute>
                        <Sidebar>
                          <AdblockBypassPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Route pour SSP Dashboard - accessible à tous les utilisateurs authentifiés */}
                  <Route
                    path="/app/ssp-dashboard"
                    element={
                      <ProtectedRoute>
                        <Sidebar>
                          <SSPDashboardPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Route pour No Consent Dashboard - accessible à tous les utilisateurs authentifiés */}
                  <Route
                    path="/app/no-consent-dashboard"
                    element={
                      <ProtectedRoute>
                        <Sidebar>
                          <NoConsentDashboardPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Routes protégées qui nécessitent le rôle ADMIN */}
                  
                  {/* Route pour Overview Dashboard - Admin seulement */}
                  <Route
                    path="/app/overview"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Sidebar>
                          <OverviewPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/app/users"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Sidebar>
                          <UserManagementPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Nouvelle route pour les prospects */}
                  <Route
                    path="/app/prospects"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Sidebar>
                          <ProspectsManagementPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/app/contacts"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Sidebar>
                          <ContactsManagementPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />
                  
                  <Route
                    path="/app/tasks"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Sidebar>
                          <TaskDashboardPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/tasks/:taskId"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Sidebar>
                          <TaskDescriptionPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/productivity-hub"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Sidebar>
                          <ProductivityHubPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/domain-services"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Sidebar>
                          <DomainServicesPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/app/admin-settings"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Sidebar>
                          <AdminSettingsPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Route pour Exchange Rates - Admin seulement */}
                  <Route
                    path="/app/exchange-rates"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Sidebar>
                          <ExchangeRatesPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />

                  {/* Route pour Adblock Legacy Dashboard - Admin seulement (peut être élargi plus tard) */}
                  <Route
                    path="/app/adblock-legacy"
                    element={
                      <ProtectedRoute requiredRole="ADMIN">
                        <Sidebar>
                          <AdblockLegacyDashboardPage />
                        </Sidebar>
                      </ProtectedRoute>
                    }
                  />
                  
                  {/* Route 404 */}
                  <Route path="*" element={<div>404 - Page not found</div>} />
                </Routes>
              </MainLayout>
            </TooltipProvider>
          </TranslationProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;